import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Chips, Dialog, useDialogClose } from '../components/ui';
import { api, errorMessage, newIdempotencyKey } from '../lib/api';
import { rupees } from '../lib/format';
import { qk } from '../lib/realtime-cache';
import type { MoneyTotals, Payout, PayoutMethod } from '../lib/types';

export const LEDGER_LABEL: Record<string, string> = {
  RIDE_FARE: 'Ride fare',
  COMMISSION: 'Commission',
  CASH_COLLECTED: 'Cash kept from rider',
  CANCELLATION_FEE: 'Cancellation fee',
  ADJUSTMENT: 'Adjustment',
  TIP: 'Tip',
  REWARD_BONUS: 'Milestone bonus',
  SETTLEMENT: 'Cash handed in',
  PAYOUT: 'Paid to the driver',
};
export const METHOD_LABEL: Record<PayoutMethod, string> = { UPI: 'UPI', BANK: 'Bank transfer', CASH: 'Cash' };

const toPaise = (s: string) => Math.round(Number(s) * 100);
const money = (v: string) => v.replace(/[^\d.]/g, '').slice(0, 9);

/** After any money action: every list and page that shows a balance reads again. */
export function useMoneyRefresh(driverId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: qk.payouts });
    void qc.invalidateQueries({ queryKey: qk.settlements });
    void qc.invalidateQueries({ queryKey: qk.driver(driverId) });
  };
}

type Who = { id: string; name: string | null };
const nameOf = (d: Who) => d.name ?? 'the driver';

function Amount({ value, onChange, label, hint }: { value: string; onChange: (v: string) => void; label: string; hint: string }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      <span className="input-affix">
        <span className="prefix">₹</span>
        <input className="input num has-prefix" inputMode="decimal" value={value} onChange={(e) => onChange(money(e.target.value))} />
      </span>
      <span className="field-hint">{hint}</span>
    </label>
  );
}

function Actions({ busy, ok, label }: { busy: boolean; ok: boolean; label: string }) {
  const close = useDialogClose();
  return (
    <div className="actions">
      <button type="button" className="btn" onClick={close}>
        Cancel
      </button>
      <button type="submit" className="btn btn-dark" disabled={busy || !ok}>
        {busy ? 'Saving…' : label}
      </button>
    </div>
  );
}

// ───── create a payout (PENDING) ─────

export function PayDialog({ driver, money: m, onClose, onDone }: { driver: Who; money: MoneyTotals; onClose: () => void; onDone: (t: string) => void }) {
  return (
    <Dialog title={`Pay ${nameOf(driver)}`} onClose={onClose}>
      <PayForm driver={driver} max={m.availableToPayPaise} onDone={onDone} />
    </Dialog>
  );
}
function PayForm({ driver, max, onDone }: { driver: Who; max: number; onDone: (t: string) => void }) {
  const close = useDialogClose();
  const refresh = useMoneyRefresh(driver.id);
  // one key for this payout: a retry after a network error sends the same key, so it can't be paid twice
  const [key] = useState(newIdempotencyKey);
  const [amount, setAmount] = useState(String(max / 100));
  const [method, setMethod] = useState<PayoutMethod>('UPI');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const paise = toPaise(amount);
  const ok = Number.isFinite(paise) && paise >= 100 && paise <= max;
  return (
    <form
      className="stack-sm"
      style={{ gap: 16 }}
      onSubmit={async (e) => {
        e.preventDefault();
        if (!ok) return;
        setBusy(true);
        setError(null);
        try {
          await api.post(
            `/admin/drivers/${driver.id}/payouts`,
            { amountPaise: paise, method, ...(reference.trim() ? { reference: reference.trim() } : {}), ...(note.trim() ? { note: note.trim() } : {}) },
            { 'Idempotency-Key': key },
          );
          refresh();
          onDone(`${rupees(paise)} payout to ${nameOf(driver)} is ready. Mark it paid once the money has gone.`);
          close();
        } catch (err) {
          setError(errorMessage(err));
          setBusy(false);
        }
      }}
    >
      <p className="muted">Zoomo Rides can pay {nameOf(driver)} up to {rupees(max)} now. The payout waits until you mark it paid.</p>
      <Chips<PayoutMethod> label="How" value={method} onChange={setMethod} options={(['UPI', 'BANK', 'CASH'] as const).map((v) => ({ value: v, label: METHOD_LABEL[v] }))} />
      <Amount value={amount} onChange={setAmount} label="Amount" hint={`Up to ${rupees(max)}.`} />
      {method !== 'CASH' && (
        <label className="field">
          <span className="field-label">UTR / reference (can be added when you mark it paid)</span>
          <input className="input" maxLength={100} value={reference} onChange={(e) => setReference(e.target.value)} />
        </label>
      )}
      <label className="field">
        <span className="field-label">Note (optional)</span>
        <input className="input" maxLength={300} value={note} placeholder="For example: week of 19 October" onChange={(e) => setNote(e.target.value)} />
      </label>
      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
      <Actions busy={busy} ok={ok} label={ok ? `Create ${rupees(paise)} payout` : 'Create payout'} />
    </form>
  );
}

// ───── mark a payout paid ─────

export function MarkPaidDialog({ payout, driverId, onClose, onDone }: { payout: Payout; driverId: string; onClose: () => void; onDone: (t: string) => void }) {
  return (
    <Dialog title={`Mark ${rupees(payout.amountPaise)} as paid`} onClose={onClose}>
      <MarkPaidForm payout={payout} driverId={driverId} onDone={onDone} />
    </Dialog>
  );
}
function MarkPaidForm({ payout, driverId, onDone }: { payout: Payout; driverId: string; onDone: (t: string) => void }) {
  const close = useDialogClose();
  const refresh = useMoneyRefresh(driverId);
  const [reference, setReference] = useState(payout.reference ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const needsRef = payout.method !== 'CASH';
  const ok = !needsRef || reference.trim().length >= 3;
  return (
    <form
      className="stack-sm"
      style={{ gap: 16 }}
      onSubmit={async (e) => {
        e.preventDefault();
        if (!ok) return;
        setBusy(true);
        setError(null);
        try {
          await api.post(`/admin/payouts/${payout.id}/mark-paid`, reference.trim() ? { reference: reference.trim() } : {});
          refresh();
          onDone(`${rupees(payout.amountPaise)} marked paid. The driver sees it in the app.`);
          close();
        } catch (err) {
          setError(errorMessage(err));
          setBusy(false);
        }
      }}
    >
      <p className="muted">
        {METHOD_LABEL[payout.method]} · created {new Date(payout.createdAt).toLocaleDateString('en-IN')}
        {payout.note ? ` · ${payout.note}` : ''}. Only mark it paid once the money has reached the driver.
      </p>
      <label className="field">
        <span className="field-label">{needsRef ? 'UTR / reference' : 'Reference (optional)'}</span>
        <input className="input" maxLength={100} value={reference} onChange={(e) => setReference(e.target.value)} />
        {needsRef && <span className="field-hint">Needed for UPI and bank transfers.</span>}
      </label>
      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
      <Actions busy={busy} ok={ok} label="Mark paid" />
    </form>
  );
}

// ───── cash handed in at the office ─────

export function CashInDialog({ driver, max, onClose, onDone }: { driver: Who; max: number; onClose: () => void; onDone: (t: string) => void }) {
  return (
    <Dialog title={`Cash in from ${nameOf(driver)}`} onClose={onClose}>
      <CashInForm driver={driver} max={max} onDone={onDone} />
    </Dialog>
  );
}
function CashInForm({ driver, max, onDone }: { driver: Who; max: number; onDone: (t: string) => void }) {
  const close = useDialogClose();
  const refresh = useMoneyRefresh(driver.id);
  const [amount, setAmount] = useState(String(max / 100));
  const [note, setNote] = useState('Cash handed in at the office');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const paise = toPaise(amount);
  const ok = Number.isFinite(paise) && paise >= 100 && paise <= max && note.trim().length >= 3;
  return (
    <form
      className="stack-sm"
      style={{ gap: 16 }}
      onSubmit={async (e) => {
        e.preventDefault();
        if (!ok) return;
        setBusy(true);
        setError(null);
        try {
          const r = await api.post<{ balancePaise: number }>(`/admin/drivers/${driver.id}/settlements`, {
            direction: 'FROM_DRIVER',
            amountPaise: paise,
            note: note.trim(),
          });
          refresh();
          onDone(`${rupees(paise)} recorded for ${nameOf(driver)}. ${r.balancePaise === 0 ? 'Fully settled.' : `Still to settle: ${rupees(Math.abs(r.balancePaise))}.`}`);
          close();
        } catch (err) {
          setError(errorMessage(err));
          setBusy(false);
        }
      }}
    >
      <p className="muted">{nameOf(driver)} holds {rupees(max)} of Zoomo Rides’ money from cash rides.</p>
      <Amount value={amount} onChange={setAmount} label="Amount" hint={`Up to ${rupees(max)}.`} />
      <label className="field">
        <span className="field-label">Note</span>
        <input className="input" maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} />
        <span className="field-hint">Saved on the driver’s ledger and in the audit log.</span>
      </label>
      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
      <Actions busy={busy} ok={ok} label={ok ? `Record ${rupees(paise)}` : 'Record'} />
    </form>
  );
}

// ───── adjustment (± with a reason) ─────

export function AdjustDialog({ driver, onClose, onDone }: { driver: Who; onClose: () => void; onDone: (t: string) => void }) {
  return (
    <Dialog title={`Adjust ${nameOf(driver)}’s balance`} onClose={onClose}>
      <AdjustForm driver={driver} onDone={onDone} />
    </Dialog>
  );
}
function AdjustForm({ driver, onDone }: { driver: Who; onDone: (t: string) => void }) {
  const close = useDialogClose();
  const refresh = useMoneyRefresh(driver.id);
  const [key] = useState(newIdempotencyKey);
  const [way, setWay] = useState<'add' | 'take'>('add');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const paise = toPaise(amount);
  const ok = Number.isFinite(paise) && paise >= 1 && paise <= 10_000_000 && reason.trim().length >= 3;
  return (
    <form
      className="stack-sm"
      style={{ gap: 16 }}
      onSubmit={async (e) => {
        e.preventDefault();
        if (!ok) return;
        setBusy(true);
        setError(null);
        try {
          await api.post(`/admin/drivers/${driver.id}/adjustments`, { amountPaise: way === 'add' ? paise : -paise, reason: reason.trim() }, { 'Idempotency-Key': key });
          refresh();
          onDone(`${way === 'add' ? '+' : '−'}${rupees(paise)} adjustment saved for ${nameOf(driver)}.`);
          close();
        } catch (err) {
          setError(errorMessage(err));
          setBusy(false);
        }
      }}
    >
      <p className="muted">A correction on the driver’s ledger. The driver sees it with your reason.</p>
      <Chips<'add' | 'take'>
        label="Direction"
        value={way}
        onChange={setWay}
        options={[
          { value: 'add', label: 'Zoomo Rides owes more (+)' },
          { value: 'take', label: 'Driver owes more (−)' },
        ]}
      />
      <Amount value={amount} onChange={setAmount} label="Amount" hint="Up to ₹1,00,000." />
      <label className="field">
        <span className="field-label">Reason</span>
        <input className="input" maxLength={300} value={reason} placeholder="For example: fuel advance" onChange={(e) => setReason(e.target.value)} />
        <span className="field-hint">At least 3 characters. Saved in the audit log.</span>
      </label>
      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
      <Actions busy={busy} ok={ok} label="Save adjustment" />
    </form>
  );
}
